import React from 'react'
import { Card, Modal, Alert, Statistic, Row, Col, Divider, Badge, Progress, Skeleton } from 'antd'
import { LoadingOutlined, CheckCircleOutlined, ExclamationCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'

// Card Wrapper Component
interface PageCardProps {
  title?: string
  subtitle?: string
  children: React.ReactNode
  loading?: boolean
  extra?: React.ReactNode
  bordered?: boolean
}

export const PageCard: React.FC<PageCardProps> = ({
  title,
  subtitle,
  children,
  loading = false,
  extra,
  bordered = true,
}) => {
  return (
    <Card
      title={title}
      extra={extra}
      bordered={bordered}
      loading={loading}
      style={{ marginBottom: '16px' }}
    >
      {subtitle && <p style={{ color: '#8c8c8c', marginBottom: '16px' }}>{subtitle}</p>}
      {children}
    </Card>
  )
}

// Alert Components
interface InfoAlertProps {
  title?: string
  message: string
  closable?: boolean
  onClose?: () => void
}

export const InfoAlert: React.FC<InfoAlertProps> = ({
  title,
  message,
  closable = true,
  onClose,
}) => (
  <Alert
    type="info"
    message={title}
    description={message}
    closable={closable}
    onClose={onClose}
    style={{ marginBottom: '16px' }}
  />
)

export const SuccessAlert: React.FC<InfoAlertProps> = ({
  title = 'Success',
  message,
  closable = true,
  onClose,
}) => (
  <Alert
    type="success"
    message={title}
    description={message}
    closable={closable}
    onClose={onClose}
    style={{ marginBottom: '16px' }}
  />
)

export const WarningAlert: React.FC<InfoAlertProps> = ({
  title = 'Warning',
  message,
  closable = true,
  onClose,
}) => (
  <Alert
    type="warning"
    message={title}
    description={message}
    closable={closable}
    onClose={onClose}
    style={{ marginBottom: '16px' }}
  />
)

export const ErrorAlert: React.FC<InfoAlertProps> = ({
  title = 'Error',
  message,
  closable = true,
  onClose,
}) => (
  <Alert
    type="error"
    message={title}
    description={message}
    closable={closable}
    onClose={onClose}
    style={{ marginBottom: '16px' }}
  />
)

// Statistic Card
interface StatisticCardProps {
  title: string
  value: string | number
  suffix?: string
  prefix?: string
  precision?: number
  valueStyle?: React.CSSProperties
}

export const StatisticCard: React.FC<StatisticCardProps> = ({
  title,
  value,
  suffix,
  prefix,
  precision = 0,
  valueStyle,
}) => (
  <Card style={{ textAlign: 'center' }}>
    <Statistic
      title={title}
      value={value}
      suffix={suffix}
      prefix={prefix}
      precision={precision}
      valueStyle={valueStyle}
    />
  </Card>
)

// KPI Grid Component
interface KPIItem {
  label: string
  value: string | number
  unit?: string
  trend?: 'up' | 'down' | 'neutral'
  percentage?: number
}

interface KPIGridProps {
  items: KPIItem[]
  columns?: number
}

export const KPIGrid: React.FC<KPIGridProps> = ({ items, columns = 4 }) => (
  <Row gutter={[16, 16]}>
    {items.map((item, index) => (
      <Col key={index} xs={24} sm={12} lg={24 / columns}>
        <Card style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px' }}>
            {item.label}
          </div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
            {item.value}
            {item.unit && <span style={{ fontSize: '14px', marginLeft: '4px' }}>{item.unit}</span>}
          </div>
          {item.percentage !== undefined && (
            <Progress
              type="circle"
              percent={item.percentage}
              width={60}
              strokeColor={item.percentage >= 80 ? '#52c41a' : '#faad14'}
            />
          )}
        </Card>
      </Col>
    ))}
  </Row>
)

// Loading Skeleton
interface SkeletonLoaderProps {
  count?: number
  type?: 'list' | 'table' | 'card'
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ count = 1, type = 'list' }) => {
  if (type === 'list') {
    return (
      <>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} style={{ marginBottom: '16px' }}>
            <Skeleton active paragraph={{ rows: 3 }} />
          </div>
        ))}
      </>
    )
  } else if (type === 'table') {
    return <Skeleton active paragraph={{ rows: 8 }} />
  } else {
    return <Skeleton active card />
  }
}

// Empty State
interface EmptyStateProps {
  title: string
  description?: string
  action?: React.ReactNode
}

export const EmptyState: React.FC<EmptyStateProps> = ({ title, description, action }) => (
  <div style={{ textAlign: 'center', padding: '48px 0' }}>
    <h2 style={{ marginBottom: '8px' }}>{title}</h2>
    {description && <p style={{ color: '#8c8c8c', marginBottom: '16px' }}>{description}</p>}
    {action && <div>{action}</div>}
  </div>
)

// Status Badge
interface StatusBadgeProps {
  status: 'success' | 'error' | 'warning' | 'info' | 'processing'
  label: string
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, label }) => {
  const statusConfig = {
    success: { color: '#52c41a', icon: <CheckCircleOutlined /> },
    error: { color: '#ff4d4f', icon: <CloseCircleOutlined /> },
    warning: { color: '#faad14', icon: <ExclamationCircleOutlined /> },
    info: { color: '#1890ff', icon: null },
    processing: { color: '#1890ff', icon: <LoadingOutlined /> },
  }

  const config = statusConfig[status]

  return (
    <Badge
      color={config.color}
      text={
        <span>
          {config.icon && <span style={{ marginRight: '4px' }}>{config.icon}</span>}
          {label}
        </span>
      }
    />
  )
}

// Confirmation Modal
interface ConfirmModalProps {
  visible: boolean
  title: string
  content: string
  okText?: string
  cancelText?: string
  onOk: () => void | Promise<void>
  onCancel: () => void
  loading?: boolean
  danger?: boolean
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  visible,
  title,
  content,
  okText = 'OK',
  cancelText = 'Cancel',
  onOk,
  onCancel,
  loading = false,
  danger = false,
}) => (
  <Modal
    title={title}
    open={visible}
    onOk={onOk}
    onCancel={onCancel}
    okText={okText}
    cancelText={cancelText}
    confirmLoading={loading}
    okButtonProps={{ danger, type: danger ? 'primary' : 'default' }}
  >
    {content}
  </Modal>
)

// Divider with Text
interface DividerProps {
  text?: string
  orientation?: 'left' | 'center' | 'right'
}

export const CustomDivider: React.FC<DividerProps> = ({ text, orientation = 'center' }) => (
  <Divider orientation={orientation}>{text}</Divider>
)

// Section Header
interface SectionHeaderProps {
  title: string
  subtitle?: string
  action?: React.ReactNode
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({ title, subtitle, action }) => (
  <div style={{ marginBottom: '24px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <h2 style={{ margin: 0, marginBottom: subtitle ? '4px' : 0 }}>{title}</h2>
        {subtitle && <p style={{ margin: 0, color: '#8c8c8c' }}>{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  </div>
)
